import React, { useState, useEffect, useContext } from "react";
import { Typography, Button, Paper, Box, Chip, Divider } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import FilePresentOutlinedIcon from "@mui/icons-material/FilePresentOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { UserContext } from "../userContext/UserContext";
import { getClientSignalHeaders } from "../../../utils/clientSignals";

const DOWNLOAD_TERMS_TEXT =
  "Ao baixar, você confirma que este arquivo digital foi entregue para seu uso pessoal e que o compartilhamento das informações não é permitido.";

function FileDownloads() {
  const [file, setFile] = useState(null);
  const [downloads, setDownloads] = useState(0);
  const [loading, setLoading] = useState(false);
  const { user } = useContext(UserContext);
  const userId = user ? user.id : null;

  const fetchFile = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/files`);
      if (response.ok) {
        const data = await response.json();
        setFile(data);
      } else {
        setFile(null);
      }
    } catch (err) {
      console.error("Erro ao buscar arquivo:", err);
      setFile(null);
    }
  };

  const fetchDownloads = async () => {
    try {
      if (!userId || !file) return;
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/files/downloads/count?user_id=${userId}&file_name=${file.filename}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      if (response.ok) {
        const data = await response.json();
        setDownloads(data.count || 0);
      } else {
        console.error("Erro ao buscar contagem de downloads:", response.statusText);
        setDownloads(0);
      }
    } catch (err) {
      console.error("Erro ao buscar contagem de downloads:", err);
      setDownloads(0);
    }
  };

  useEffect(() => {
    fetchFile();
  }, []); // Carrega o arquivo apenas uma vez ao montar o componente
  
  useEffect(() => {
    if (userId && file?.filename) {
      fetchDownloads();
    }
  }, [userId, file]); // Só chama `fetchDownloads()` quando `file.filename` estiver disponível
  

  const handleDownload = async () => {
    if (!userId || loading || downloads >= 5) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/files/download?user_id=${userId}`,
        {
          headers: {
            ...getClientSignalHeaders(),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Erro ao processar o download.");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_filename || file.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      fetchDownloads();
    } catch (error) {
      console.error("Erro ao fazer download:", error);
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const whatsappLink = `https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}?text=Olá, tenho uma dúvida sobre os downloads.`;

  return (
    <Paper
      sx={{
        p: { xs: 2.2, md: 3 },
        bgcolor: "rgba(15,22,33,0.94)",
        borderRadius: 3,
        border: "1px solid rgba(255,255,255,0.1)",
        boxShadow: "0 16px 40px rgba(0,0,0,0.32)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.2, mb: 1.4 }}>
        <Typography variant="h6" gutterBottom sx={{ color: "#F4F9FF", fontWeight: 800, mb: 0 }}>
          Biblioteca de Atualizações
        </Typography>
        <Chip
          icon={<ShieldOutlinedIcon />}
          label="Uso pessoal"
          size="small"
          sx={{
            color: "rgba(231,240,250,0.88)",
            bgcolor: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.15)",
          }}
        />
      </Box>
      <Typography variant="body2" sx={{ color: "rgba(231,240,250,0.72)", mb: 2.2 }}>
        Baixe o arquivo mensal e mantenha seus indicadores atualizados.
      </Typography>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 2 }} />
      {file ? (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <FilePresentOutlinedIcon sx={{ color: "#9FC7FF" }} />
            <Typography variant="subtitle1" sx={{ color: "#F4F9FF", fontWeight: 700 }}>
              {file.original_filename || file.filename}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: "rgba(231,240,250,0.74)" }}>
            Última atualização: {new Date(file.uploaded_at).toLocaleString()}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.8, color: "rgba(231,240,250,0.74)" }}>
            Downloads neste mês: <strong>{downloads}</strong> / 5
          </Typography>

          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              border: "1px solid rgba(255,180,138,0.28)",
              bgcolor: "rgba(255,180,138,0.08)",
            }}
          >
            <Typography variant="body2" sx={{ color: "#FFD2B7", lineHeight: 1.55 }}>
              {DOWNLOAD_TERMS_TEXT}
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={handleDownload}
            disabled={downloads >= 5 || loading}
            startIcon={<DownloadOutlinedIcon />}
            sx={{
              mt: 2.2,
              textTransform: "none",
              fontWeight: 800,
              borderRadius: 2,
              px: 2.2,
              py: 1,
              background: "linear-gradient(90deg, #2E78E6 0%, #4E9AFD 100%)",
              "&:hover": { background: "linear-gradient(90deg, #3A85F0 0%, #62A8FF 100%)" },
            }}
          >
            {loading ? "Processando..." : "Baixar arquivo"}
          </Button>

          {downloads >= 5 && (
            <>
              <Button
                variant="outlined"
                color="success"
                href={whatsappLink}
                target="_blank"
                startIcon={<WhatsAppIcon />}
                sx={{ mt: 2, textTransform: "none", fontWeight: 700, borderRadius: 2 }}
              >
                Falar no WhatsApp
              </Button>
              <Typography
                variant="body2"
                sx={{
                  color: "#FF9AA5",
                  mt: 1.4,
                }}
              >
                Você atingiu o limite mensal de downloads. Caso precise de apoio, fale conosco.
              </Typography>
            </>
          )}
        </Box>
      ) : (
        <Typography variant="body2" sx={{ color: "rgba(231,240,250,0.72)" }}>
          Nenhum arquivo disponível no momento.
        </Typography>
      )}
    </Paper>
  );
}

export default FileDownloads;
